/**
 * Imports the necessary components and libraries for rendering a smart table
 * with translation support using React.
 * @module React
 * @module CSmartTable
 * @module useTranslation
 * @module Card
 * @requires "@coreui/react-pro"
 * @requires "react-i18next"
 * @requires "react-bootstrap"
 */
import { CSmartTable } from "@coreui/react-pro";
import moment from "moment";
import React from "react";
import Card from "react-bootstrap/Card";
import { useTranslation } from "react-i18next";

/**
 * Import the necessary components and utilities for the current module.
 * @import {CustomLoadingHTML} from "../../../components/customLoadingHTML/customLoadingHTML";
 * @import {CustomToggleButton} from "../../../components/customToggleButton/customToggleButton";
 * @import {ConfirmModal} from "../../../components/confirmModal/confirmModel";
 * @import {EmptyCell} from "../../../components/emptyCell/emptyCell";
 * @import {Constant} from "../../../util/constant";
 */
import CustomLoadingHTML from "../../../../components/customLoadingHTML/customLoadingHTML";
import EmptyCell from "../../../../components/emptyCell/emptyCell";
import ConfirmModal from "../../../confirmModal/confirmModel";
import { TicketOrderAddonSnapshot } from "../../../clubCatalog/BookingCatalogSnapshots";
import {
  formatCatalogMoney,
  orderAddonTotal,
} from "../../../../helpers/clubCatalog";
import AvatarImageWithFallback from "../../../../components/avatarImageWithFallback/AvatarImageWithFallback";

import {
  getCloudinaryMediaBaseUrl,
  getCloudinaryMobileDeliveryBase,
} from "../../../../config/cloudinaryBase";
import { resolveCloudinaryImageSrc } from "../../../../helpers/helper.service";
import Constant from "../../../../util/constant";

import "./ticketStatusTab.css";

const TABLE_BODY_MIN_SCROLL_PX = 280;
const TABLE_BODY_MAX_SCROLL_PX = 420;

const TicketStatusTabComponent = (props) => {
  /**
   * Destructures the necessary props from the `props` object and assigns them to
   * individual variables for easier access and readability.
   * @param {{object}} props - The props object containing all the necessary data.
   * @returns None
   */
  const { t } = useTranslation();

  const {
    orderData,
    columns,
    placeHolderShimer,
    noDataTitle,
    noDataDesc,
    clearSearchButton,
    smarttableref,
    noDataFound,
    noDataAddButtonTitle,
    emptyDataAddButton,
    dimensions,
    breadcrumbHeight,
    loading,
    tableContainerRef,
    loaderHtml,
    emptyImg,
    emptyTitle,
    emptyDataDesc,
    showViewTicketModal,
    onViewTicket,
    closeViewTicketModal,
    openviewTicketModal,
    ticketData,
    itemsPerPage,
    eventDetails,
    emptyDataDescTow,
    // onCheckChange
  } = props;

  /**
   * Constructs the Cloudinary URL by concatenating the environment variables
   * for the Cloudinary URL, name, and subfolder.
   * @returns The constructed Cloudinary URL.
   */
  const CLOUDINARY_URL = getCloudinaryMediaBaseUrl();
  const CLOUDINARY_URL_MOBILE = getCloudinaryMobileDeliveryBase();
  const breadcrumbOffset = Number(breadcrumbHeight) || 0;
  const loaderOffset = loading && loaderHtml ? Number(loaderHtml) : 0;
  const viewportGap = dimensions.width > 1279 ? 150 : 250;
  const tableBodyScrollHeight = Math.max(
    TABLE_BODY_MIN_SCROLL_PX,
    Math.min(
      TABLE_BODY_MAX_SCROLL_PX,
      dimensions.height - breadcrumbOffset - loaderOffset - viewportGap
    )
  );
  return (
    <React.Fragment>
      <div className="ticketStatusTab">
        <div className="ticketStatus">
          <Card className="mainCard mainCardWrapper">
            <Card.Body className="p-0 activeTab">
              <div className="mainCardBody">
                {/* <div className="Card-Button">
                </div> */}
                {/**
                 * Renders a smart table component with custom column rendering and filtering options.
                 * @param {{boolean}} cleaner - Whether to enable the cleaner feature.
                 * @param {{boolean}} clickableRows - Whether to make the rows clickable.
                 * @param {{Array}} columns - The array of column objects to display in the table.
                 * @param {{boolean}} columnFilter - Whether to enable column filtering.
                 * @param {{Array}} items - The array of items to display in the table.
                 * @param {{number}} itemsPerPage - The number of items to display per page.
                 * @param {{Object}} scopedColumns - An object mapping column names to custom rendering functions.
                 * @param {{function}} onTableFilterChange - The function to call
                 */}
                <CSmartTable
                  ref={smarttableref}
                  cleaner
                  clickableRows
                  columns={columns}
                  columnFilter={false}
                  items={Array.isArray(orderData) ? orderData : []}
                  itemsPerPage={itemsPerPage}
                  scopedColumns={{
                    userid: (item) => {
                      const userImage = item?.userid?.image;
                      const profileSrc = userImage
                        ? resolveCloudinaryImageSrc(
                            userImage,
                            CLOUDINARY_URL_MOBILE || CLOUDINARY_URL
                          )
                        : "";
                      return (
                        <td>
                          <div className="tdWrapper profilePic">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            <AvatarImageWithFallback
                              src={profileSrc}
                              alt={item?.userid?.fullname || "profile"}
                              width={32}
                              height={32}
                            />
                            <span className="ticketUserName">
                              {item?.userid?.fullname}
                            </span>
                          </div>
                        </td>
                      );
                    },
                    noofticket: (item) => {
                      return (
                        <td>
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.noofticket}
                          </div>
                        </td>
                      );
                    },
                    tickettype: (item) => {
                      return (
                        <td>
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.tickettype}
                          </div>
                        </td>
                      );
                    },
                    amount: (item) => {
                      return (
                        <td>
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            ₹ {item?.amount}
                          </div>
                        </td>
                      );
                    },
                    addonLines: (item) => {
                      const total = orderAddonTotal(item);
                      return (
                        <td>
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {total
                              ? formatCatalogMoney(total)
                              : "—"}
                          </div>
                        </td>
                      );
                    },
                    // Home delivery column — hidden for now
                    // isdelivery: (item) => {
                    //   return (
                    //     <td>
                    //       <div
                    //         className="actionWrapper"
                    //         onClick={(e) => e.stopPropagation()}
                    //       >
                    //         {placeHolderShimer && (
                    //           <div className="shimer"></div>
                    //         )}
                    //         <div className="form-check">
                    //           <CheckBox
                    //             checkboxClass="form-check-input"
                    //             id="customControlInline"
                    //             labelTitle={t("")}
                    //             checkedState={item?.isdelivery}
                    //           ></CheckBox>
                    //         </div>
                    //       </div>
                    //     </td>
                    //   );
                    // },
                    orderstatus: (item) => {
                      return (
                        <td>
                          <div
                            className={
                              item?.orderstatus === Constant.GENERALKEYS.PAID
                                ? "paid tdWrapper"
                                : item?.orderstatus ===
                                  Constant.GENERALKEYS.REFUNDED
                                ? "refund tdWrapper"
                                : item?.orderstatus ===
                                  Constant.GENERALKEYS.FAIL
                                ? "fail tdWrapper"
                                : item?.orderstatus ===
                                  Constant.GENERALKEYS.CANCELLED
                                ? "cancel tdWrapper"
                                : item?.orderstatus ===
                                  Constant.GENERALKEYS.PENDING
                                ? "paid tdWrapper"
                                : "tdWrapper"
                            }
                          >
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {item?.orderstatus}
                          </div>
                        </td>
                      );
                    },
                    // Ticket ID column — hidden for now
                    // ticketid: (item) => {
                    //   return (
                    //     <td>
                    //       <div className="tdWrapper">
                    //         {placeHolderShimer && (
                    //           <div className="shimer"></div>
                    //         )}
                    //         {item?.ticketid || "-"}
                    //       </div>
                    //     </td>
                    //   );
                    // },
                    orderstatustimestamp: (item) => {
                      return (
                        <td>
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {moment(item?.orderstatustimestamp).format(
                              "DD MMM YY, h:mm A"
                            )}
                          </div>
                        </td>
                      );
                    },
                    viewTicket: (item) => {
                      return (
                        <td>
                          <div className="tdWrapper">
                            {placeHolderShimer && (
                              <div className="shimer"></div>
                            )}
                            {!item?.ticketid ? (
                              " "
                            ) : (
                              <div
                                className="tdWrapper ticketArrow"
                                role="button"
                                tabIndex={0}
                                title={t("VIEW_TICKET")}
                                aria-label={t("VIEW_TICKET")}
                                onClick={(e) => {
                                  openviewTicketModal(e, item);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    openviewTicketModal(e, item);
                                  }
                                }}
                              >
                                <i className="bx bx-chevron-right" />
                              </div>
                            )}
                          </div>
                        </td>
                      );
                    },
                  }}
                  tableProps={{
                    className: "mainTable",
                    responsive: true,
                    striped: false,
                    hover: false,
                  }}
                  noItemsLabel={
                    !placeHolderShimer && (
                      <EmptyCell
                        emptyItems={orderData}
                        noDataTitle={
                          orderData?.length === 0 ? noDataFound : noDataTitle
                        }
                        emptyImg={emptyImg}
                        emptyTitle={emptyTitle}
                        emptyDataDesc={emptyDataDesc}
                        emptyDataDescTow={emptyDataDescTow}
                        noDataAddButton={true}
                        noDataAddButtonTitle={noDataAddButtonTitle}
                        noDataDesc={noDataDesc}
                        emptyDataAddButton={emptyDataAddButton}
                        clearSearchButton={clearSearchButton}
                      />
                    )
                  }
                  tableBodyProps={{
                    className: "align-middle Table-Body-scroll main",
                    style: {
                      height: tableBodyScrollHeight,
                    },
                    ref: tableContainerRef,
                  }}
                />
                {loading && <CustomLoadingHTML />}
              </div>
            </Card.Body>
          </Card>
        </div>
      </div>
      <ConfirmModal
        className="viewTicketModal"
        isModelIcon={false}
        onHide={closeViewTicketModal}
        isCloseBtn={true}
        closeModelIcon={() => closeViewTicketModal()}
        isUnderline={false}
        showModal={showViewTicketModal}
        isFirstButton={false}
        isSecondButton={false}
        onProcced={onViewTicket}
        rightButtonClick={closeViewTicketModal}
        modalContent={false}
        badgeDate={true}
        modalTitleDiv={false}
        ticketData={ticketData}
        date={moment(ticketData?.orderstatustimestamp).format(
          "DD MMM YY, hh:mm A"
        )}
        status={ticketData?.orderstatus}
      >
        <div className="viewTicketBody">
          <div className="bodyUpper">
            <span className="ticketTitle">{eventDetails?.title}</span>
            <span className="ticketId">
              {t("TICKETID")}: {ticketData?.ticketid}
            </span>

            <span className="ticketTime">
              {t("EVENT_TIMINGS")}:{" "}
              {moment(eventDetails?.startdatetime).format("DD MMM YY, h:mm A")}
            </span>
            <span className="ticketBarcode">
              <img
                src={`data:image/jpeg;base64,${ticketData?.qrcode}`}
                alt="barcode"
              />
            </span>
          </div>
          <div className="bodyBottom">
            <div className="pricewrapper addressRowStyle">
              <span className="bottomlabel">{t("HOME_DELIVERY_ADDRESS")}</span>
              <span className="bottomValue">
                {ticketData?.address ? ticketData?.address : "-"}
              </span>
            </div>
            <div className="pricewrapper">
              <span className="bottomlabel">{t("TICKET_TYPE")}</span>
              <span className="bottomValue">{ticketData?.tickettype}</span>
            </div>
            <div className="pricewrapper">
              <span className="bottomlabel">{t("TICKET_PRICE")}</span>
              <span className="bottomValue">
                {ticketData?.noofticket} x ₹{" "}
                {ticketData?.ticketprice ? ticketData?.ticketprice : 0}
              </span>
            </div>
            <div className="pricewrapper bottomLineStyle">
              <span className="bottomlabel">{t("BOOKING_FEE")}</span>
              <span className="bottomValue">
                + ₹ {ticketData?.baseamount ? ticketData?.baseamount : 0}
              </span>
            </div>
            <div className="pricewrapper">
              <span className="bottomlabel">{t("HOME_DELIVERY_CHARGE")}</span>
              <span className="bottomValue">
                + ₹{" "}
                {ticketData?.isdelivery === false
                  ? "0"
                  : ticketData?.deliveryprice}
              </span>
            </div>
            <div className="pricewrapper">
              <span className="bottomlabel">{t("BASE_PRICE")}</span>
              <span className="bottomValue">
                + ₹ {ticketData?.baseprice ? ticketData?.baseprice : 0}
              </span>
            </div>
            <div className="pricewrapper">
              <span className="bottomlabel">{t("CGST")}</span>
              <span className="bottomValue">
                ₹ {ticketData?.gst ? ticketData?.gst : 0}
              </span>
            </div>
            <div className="pricewrapper bottomLineStyle">
              <span className="bottomlabel">{t("SGST")}</span>
              <span className="bottomValue">
                ₹ {ticketData?.gst ? ticketData?.gst : 0}
              </span>
            </div>
            <div className="pricewrapper">
              <span className="bottomlabel totalAmount">
                {t("TOTAL_AMOUNT")}
              </span>
              <span className="bottomValue totalAmount">
                ₹ {ticketData?.amount}
              </span>
            </div>
            <TicketOrderAddonSnapshot order={ticketData} />
          </div>
        </div>
      </ConfirmModal>
    </React.Fragment>
  );
};

export default TicketStatusTabComponent;
